import { takeLatest, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { WebSocketsTypesEnum } from '@dynamika-shared/services/communication/src/webSockets/webSockets.type';

import { dispatch } from '../helpers';
import { actions as readerCacheActions } from './readerCache.slice';
import { authSlice } from '../auth/auth.slice';
import { clinicalTrialContextSlice } from '../clinicalTrialContext/clinicalTrialContext.slice';
import { readerTaskOrchestrationSlice } from '../readerTaskOrchestration/readerTaskOrchestration.slice';

type OrchestrationPayload = {
  taskId: string;
  clinicalTrialId: string;
  subTaskId: string;
};

/**
 * 🧹 readerCacheInvalidationSaga — clears cached reader data when it goes stale.
 *
 * The reader cache (readerCache.slice) keeps trial / visit data in memory so
 * visit switches are fast. This saga is the "when to forget" side: each watcher
 * below listens for an event meaning some cached data is no longer valid and
 * drops exactly that part. Eviction is event-driven only — never on a timer.
 *
 *   onLogout         — user logged out          → drop everything
 *   onOpenParentTask — opened a different task   → drop the previous task's cache
 *   onSwitchTrial    — switched clinical trial   → drop the previous trial's cache
 *   onTaskUpdatedWS  — server says a visit changed → drop just that visit
 */

// User logged out → forget the whole reader cache.
export function* onLogout() {
  yield dispatch(readerCacheActions.flushAll());
}

// Opened a different parent task → forget the previous task's cached visits.
export function* onOpenParentTask(action: PayloadAction<OrchestrationPayload>) {
  const prevId: string | undefined = yield select(
    (state: { readerTaskOrchestration?: { taskId?: string } }) =>
      state.readerTaskOrchestration?.taskId
  );
  const nextId = action.payload?.taskId;
  if (prevId && nextId && prevId !== nextId) {
    yield dispatch(readerCacheActions.flushParentTask({ parentTaskId: prevId }));
  }
}

// Switched to a different clinical trial → forget the previous trial's cache.
export function* onSwitchTrial(action: PayloadAction<string>) {
  const prevId: string | undefined = yield select(
    (state: { clinicalTrialContext?: { data?: { id?: string } } }) =>
      state.clinicalTrialContext?.data?.id
  );
  if (prevId && prevId !== action.payload) {
    yield dispatch(readerCacheActions.flushTrial({ clinicalTrialId: prevId }));
  }
}

// Server pushed a "task.updated" WebSocket event → forget just that one visit
// so it is re-fetched fresh next time.
export function* onTaskUpdatedWS(
  action: PayloadAction<{ taskId?: string; parentTaskId?: string } | undefined>
) {
  const { taskId, parentTaskId } = action.payload ?? {};
  if (!parentTaskId || !taskId) return;
  yield dispatch(
    readerCacheActions.flushSubtask({ parentTaskId, subTaskId: taskId })
  );
}

export function* readerCacheInvalidationSaga() {
  yield takeLatest(authSlice.actions.clearUser.type, onLogout);
  yield takeLatest(readerTaskOrchestrationSlice.actions.load.type, onOpenParentTask);
  yield takeLatest(readerTaskOrchestrationSlice.actions.start.type, onOpenParentTask);
  yield takeLatest(
    clinicalTrialContextSlice.actions.fetchClinicalTrialContext.type,
    onSwitchTrial
  );
  yield takeLatest(
    `${WebSocketsTypesEnum.WS__RECEIVED_MESSAGE}/task.updated` as string,
    onTaskUpdatedWS
  );
}
